import React, { useEffect, useState, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import SignatureManager from "../../components/SignatureManager";
import ReviewManager from "../../components/ReviewManager";

const FuelCard = () => {
  const navigate = useNavigate();
  const { id: paramId } = useParams();
  const loggedInId = localStorage.getItem("userId");
  const userRole = localStorage.getItem("userRole");
  const isAdmin = userRole === "1";
  const userId = isAdmin && paramId ? paramId : loggedInId;

  const [isSigned, setIsSigned] = useState(false);
  const [files, setFiles] = useState([]);
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [expiryDate, setExpiryDate] = useState("");
  const [fcNumber, setFcNumber] = useState("");
  const [filesToDelete, setFilesToDelete] = useState([]);

  const fileInputRef = React.useRef(null);
  const API_BASE = process.env.REACT_APP_API_BASE_URL;

  // 🔍 Fetch Fuel Card
  const fetchFuelCard = useCallback(async () => {
    if (!userId) return;

    try {
      const res = await axios.get(`${API_BASE}/api/fuelcard/${userId}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
      });

      setFcNumber(res.data.fc_number || "");
      setExpiryDate(
        res.data.fc_expiry_date
          ? new Date(res.data.fc_expiry_date).toISOString().split("T")[0]
          : ""
      );
      setUploadedFiles(res.data.files || []);
    } catch (err) {
      console.error("Fetch failed", err);
    }
  }, [API_BASE, userId]);

  useEffect(() => {
    fetchFuelCard();
  }, [fetchFuelCard]);

  // 🔍 Signature Status
  useEffect(() => {
    const fetchTaskStatus = async () => {
      try {
        const res = await axios.get(
          `${API_BASE}/api/usertasks/status/${userId}`,
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("token")}`,
            },
          }
        );

        const TASK_ID = 44;
        const status =
          res.data.status?.[String(TASK_ID)] ??
          res.data.status?.fuel_card ??
          "not_completed";

        setIsSigned(status === "review" || status === "completed");
      } catch (err) {
        console.error("Error checking signature status:", err);
      }
    };

    if (userId) fetchTaskStatus();
  }, [userId, API_BASE]);

  const markFileForDelete = (fileId) => {
    setFilesToDelete((prev) => [...prev, fileId]);
    setUploadedFiles((prev) => prev.filter((f) => f.id !== fileId));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const formData = new FormData();
    formData.append("userId", userId);
    formData.append("fc_expiry_date", expiryDate);
    formData.append("fc_number", fcNumber);
    formData.append("filesToDelete", JSON.stringify(filesToDelete));

    files.forEach((file) => {
      formData.append("files", file);
    });

    try {
      const response = await axios.post(
        `${API_BASE}/api/fuelcard/upload?userId=${userId}`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );

      alert(response.data.message || "Saved successfully!");

      setFiles([]);
      setFilesToDelete([]);

      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }

      fetchFuelCard();
    } catch (error) {
      console.error("Upload error:", error);
      alert(error.response?.data?.message || "Save failed.");
    }
  };

  return (
    <>
      {/* Header */}
      <div className="d-flex justify-content-between align-items-center pt-3">
        <span style={{ fontSize: "18px", fontWeight: "500" }}>Fuel Card</span>
        <button className="btn btn-secondary" onClick={() => navigate(-1)}>
          Return
        </button>
      </div>

      <div className="form-wrapper my-4">
        <div className="row align-items-center mb-4">
          <div className="col-4" />
          <div className="col-4 text-center">
            <h3 className="mb-0">Fuel Card</h3>
          </div>
          <div className="col-4 text-end">
            <div>New-Way Trucking Ltd</div>
            <div>7725 48 St. SE, Calgary</div>
            <div>Alberta, Canada T2C 2V3</div>
          </div>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="row">
            <div className="col-md-4 mb-3">
              <label>Fuel Card Number *</label>
              <input
                type="text"
                className="form-control"
                value={fcNumber}
                onChange={(e) => setFcNumber(e.target.value)}
                disabled={!isAdmin && isSigned}
                required
              />
            </div>

            <div className="col-md-4 mb-3">
              <label>Expiry Date *</label>
              <input
                type="date"
                className="form-control"
                value={expiryDate}
                onChange={(e) => setExpiryDate(e.target.value)}
                disabled={!isAdmin && isSigned}
                required
              />
            </div>

            <div className="col-md-12 mb-3">
              <label>Select File (PDF / JPG / PNG)</label>
              <input
                type="file"
                className="form-control"
                multiple
                accept=".pdf,.jpg,.jpeg,.png"
                ref={fileInputRef}
                onChange={(e) => setFiles([...e.target.files])}
                disabled={!isAdmin && isSigned}
              />
            </div>
          </div>

          {uploadedFiles.length > 0 && (
            <div className="p-3 bg-light rounded border">
              <div className="text-muted mb-2">Uploaded Files</div>

              {uploadedFiles.map((file) => (
                <div
                  key={file.id}
                  className="d-flex justify-content-between align-items-center mb-2"
                >
                  <div>
                    <a
                      href={`${API_BASE}/uploads/fuelcards/${file.path}`}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {file.original_name}
                    </a>
                    <div className="text-muted">
                      Uploaded on: {new Date(file.uploaded_at).toLocaleString()}
                    </div>
                  </div>

                  <button
                    type="button"
                    className="btn btn-sm btn-outline-danger"
                    disabled={!isAdmin && isSigned}
                    onClick={() => markFileForDelete(file.id)}
                  >
                    X
                  </button>
                </div>
              ))}
            </div>
          )}

          <div className="mt-4 d-flex justify-content-end">
            <button
              type="submit"
              className="btn btn-primary"
              disabled={!isAdmin && isSigned}
            >
              Save
            </button>
          </div>
        </form>

        <hr className="my-5" />

        <SignatureManager
          taskName="fuel_card"
          taskId={44}
          userId={userId}
          isAdmin={isAdmin}
        />

        <ReviewManager
          taskName="fuel_card"
          taskId={44}
          userId={userId}
          isAdmin={isAdmin}
        />
      </div>
    </>
  );
};

export default FuelCard;
